import React, { useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Stack, Typography } from '@mui/material';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import DeleteConfirmationDialog from '../../DeleteConfirmationDialog';
import type { MediaImage, PropertyRoomTypeWithImages } from '../types';
import useImageDropUpload from '../useImageDropUpload';
import { UPLOAD_TILE_DRAG_LABEL, canPersistRoomImageHiddenState, uploadButtonDragActiveSx } from '../utils';

import RoomImageSlot from './Slot';
import type { RoomImage, RoomImagesByKey } from './utils';

const INITIAL_VISIBLE_ROOM_IMAGE_COUNT = 4;

interface Props {
  hiddenImagePublicIds: Array<string>;
  isMetadataEditable?: boolean;
  mediaImages: Array<MediaImage>;
  selectedImagePublicIds: Array<string>;
  onEditImage: (publicId: string) => void;
  onOpenImagesVideo?: () => void;
  onRemoveImageFromRoom: (image: MediaImage) => Promise<void> | void;
  onReorderRoomImages: (roomKey: string, orderedPublicIds: Array<string>) => Promise<void> | void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
  onToggleSelected: (publicId: string) => void;
  onUpdateImageTitle?: (roomKey: string, publicId: string, title: string) => void;
  onUploadImagesToRoom?: (roomId: string, roomName: string, files: Array<File>) => void;
  room: PropertyRoomTypeWithImages;
  roomImages: Array<RoomImage>;
  roomKey: string;
  setRoomImagesByKey: React.Dispatch<React.SetStateAction<RoomImagesByKey>>;
  showRoomDetails?: boolean;
}

export default function ImagesByRoomRow({
  hiddenImagePublicIds,
  isMetadataEditable = false,
  mediaImages,
  selectedImagePublicIds,
  onEditImage,
  onOpenImagesVideo,
  onRemoveImageFromRoom,
  onReorderRoomImages,
  onToggleHiddenImage,
  onToggleSelected,
  onUpdateImageTitle,
  onUploadImagesToRoom,
  room,
  roomImages,
  roomKey,
  setRoomImagesByKey,
  showRoomDetails = true,
}: Props) {
  const [imagePendingDeletion, setImagePendingDeletion] = useState<MediaImage>();
  const [areAllImagesVisible, setAreAllImagesVisible] = useState(false);
  const roomName = room.name || 'Room';
  const hasExpandableImages = roomImages.length >= INITIAL_VISIBLE_ROOM_IMAGE_COUNT;
  const visibleRoomImages = areAllImagesVisible ? roomImages : roomImages.slice(0, INITIAL_VISIBLE_ROOM_IMAGE_COUNT);
  const shouldShowPickerSlot = areAllImagesVisible || !hasExpandableImages;
  const canUploadDirectly = !!onUploadImagesToRoom;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleConfirmDeleteImage = async () => {
    if (!imagePendingDeletion) {
      return;
    }

    try {
      await onRemoveImageFromRoom(imagePendingDeletion);
      setRoomImagesByKey((currentRoomImagesByKey) => ({
        ...currentRoomImagesByKey,
        [roomKey]: (currentRoomImagesByKey[roomKey] ?? []).filter(
          (image) => image.publicId !== imagePendingDeletion.publicId,
        ),
      }));
      setImagePendingDeletion(undefined);
    } catch {
      // Error already handled upstream.
    }
  };

  const handleDeleteImage = (publicId: string) => {
    const image =
      mediaImages.find((mediaImage) => mediaImage.publicId === publicId && mediaImage.sourceRoomId === roomKey) ??
      mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (image) {
      setImagePendingDeletion(image);
    }
  };

  const handleUpdateImageTitle = (publicId: string, title: string) => {
    setRoomImagesByKey((currentRoomImagesByKey) => ({
      ...currentRoomImagesByKey,
      [roomKey]: (currentRoomImagesByKey[roomKey] ?? []).map((image) =>
        image.publicId === publicId ? { ...image, title } : image,
      ),
    }));
    onUpdateImageTitle?.(roomKey, publicId, title);
  };

  const handleUploadImagesToRoom = (files: Array<File>) => {
    onUploadImagesToRoom?.(roomKey, roomName, files);
  };

  const { dragDropProps, isDragActive } = useImageDropUpload(canUploadDirectly ? handleUploadImagesToRoom : undefined);
  const addImagesLabel = isDragActive ? UPLOAD_TILE_DRAG_LABEL : 'Add images';

  const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    handleUploadImagesToRoom(files);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = roomImages.findIndex((image) => image.publicId === active.id);
    const newIndex = roomImages.findIndex((image) => image.publicId === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const nextRoomImages = arrayMove(roomImages, oldIndex, newIndex);

    setRoomImagesByKey((currentRoomImagesByKey) => ({
      ...currentRoomImagesByKey,
      [roomKey]: nextRoomImages,
    }));
    void onReorderRoomImages(
      roomKey,
      nextRoomImages.map((image) => image.publicId),
    );
  };

  return (
    <>
      <Box sx={{ py: showRoomDetails ? 3 : 0 }}>
        {!showRoomDetails && (
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Carousel order is used on the offer page. Drag images to reorder. Use the empty slot to upload an image.
              Hover an image to edit, hide, or delete.
            </Typography>
            {(canUploadDirectly || onOpenImagesVideo) && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', lg: 'auto' } }}>
                {canUploadDirectly && (
                  <Button
                    component="label"
                    variant="text"
                    startIcon={<AddPhotoAlternateOutlinedIcon />}
                    {...dragDropProps}
                    sx={{
                      whiteSpace: 'nowrap',
                      ...(isDragActive ? uploadButtonDragActiveSx : undefined),
                    }}
                  >
                    {addImagesLabel}
                    <input
                      hidden
                      accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
                      multiple
                      type="file"
                      onChange={handleUploadInputChange}
                    />
                  </Button>
                )}
                {onOpenImagesVideo && (
                  <Button
                    variant="text"
                    startIcon={<OpenInNewIcon />}
                    onClick={onOpenImagesVideo}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Open Images & Video
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: showRoomDetails ? { xs: '1fr', lg: '220px minmax(0, 1fr)' } : '1fr',
            gap: { xs: 2, lg: 3 },
          }}
        >
          {showRoomDetails && (
            <Stack spacing={1.5} alignItems="flex-start">
              <Box>
                <Typography variant="h6">{roomName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {roomImages.length} {roomImages.length === 1 ? 'image' : 'images'}
                </Typography>
              </Box>
              {canUploadDirectly && (
                <Button
                  component="label"
                  variant="text"
                  startIcon={<AddPhotoAlternateOutlinedIcon />}
                  {...dragDropProps}
                  sx={{
                    px: 0,
                    ...(isDragActive ? uploadButtonDragActiveSx : undefined),
                  }}
                >
                  {addImagesLabel}
                  <input
                    hidden
                    accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
                    multiple
                    type="file"
                    onChange={handleUploadInputChange}
                  />
                </Button>
              )}
            </Stack>
          )}

          <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
            <Stack spacing={2}>
              <SortableContext items={visibleRoomImages.map((image) => image.publicId)} strategy={rectSortingStrategy}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 2,
                  }}
                >
                  {visibleRoomImages.map((image, index) => (
                    <RoomImageSlot
                      key={image.publicId}
                      canToggleHidden={mediaImages.some(
                        (mediaImage) =>
                          mediaImage.publicId === image.publicId && canPersistRoomImageHiddenState(mediaImage),
                      )}
                      hiddenImagePublicIds={hiddenImagePublicIds}
                      image={image}
                      isMetadataEditable={isMetadataEditable}
                      selectedImagePublicIds={selectedImagePublicIds}
                      onDeleteImage={handleDeleteImage}
                      onEditImage={onEditImage}
                      onToggleHiddenImage={onToggleHiddenImage}
                      onToggleSelected={onToggleSelected}
                      onUpdateImageTitle={handleUpdateImageTitle}
                      onUploadImages={canUploadDirectly ? handleUploadImagesToRoom : undefined}
                      slotNumber={index + 1}
                    />
                  ))}
                  {shouldShowPickerSlot && (
                    <RoomImageSlot
                      hiddenImagePublicIds={hiddenImagePublicIds}
                      isMetadataEditable={isMetadataEditable}
                      selectedImagePublicIds={selectedImagePublicIds}
                      onDeleteImage={handleDeleteImage}
                      onEditImage={onEditImage}
                      onToggleHiddenImage={onToggleHiddenImage}
                      onToggleSelected={onToggleSelected}
                      onUpdateImageTitle={handleUpdateImageTitle}
                      onUploadImages={canUploadDirectly ? handleUploadImagesToRoom : undefined}
                      slotNumber={visibleRoomImages.length + 1}
                    />
                  )}
                </Box>
              </SortableContext>
              {hasExpandableImages && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setAreAllImagesVisible((isVisible) => !isVisible)}
                >
                  {areAllImagesVisible ? 'Show less' : 'Show more'}
                </Button>
              )}
            </Stack>
          </DndContext>
        </Box>
      </Box>
      <DeleteConfirmationDialog
        body={`This will delete the image from ${roomName}. This action cannot be undone.`}
        open={!!imagePendingDeletion}
        title="Delete image from room?"
        onClose={() => setImagePendingDeletion(undefined)}
        onConfirm={handleConfirmDeleteImage}
      />
    </>
  );
}
